import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { EmptyState } from '../components/EmptyState';
import { resubmitPi, piErrorMessage } from '../lib/piActions';
import { Receipt, Check, X, FilePdf, CurrencyInr, Clock, CalendarBlank, Wrench } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import useAuthStore from '../store/authStore';
import SummaryCards, { inr } from '../components/SummaryCards';
import PipelineToolbar from '../components/PipelineToolbar';
import { computeCloseWindow, withinCloseWindow, sortRows } from '../lib/pipelineFilters';
import { inrInWords } from '../lib/inrWords';
import ShareMenu from '../components/ShareMenu';
import { confirm } from '../components/ConfirmDialog';

import { notify } from '../lib/notify';
import { TableRowsSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};

const ProformaInvoices = () => {
  const { user } = useAuthStore();
  const [scope, setScope] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [decision, setDecision] = useState(null);
  const [decisionReason, setDecisionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Pipeline toolbar — sort/filter PIs by parent-lead close-date + amount.
  const [closeWindow, setCloseWindow] = useState('all');
  const [sortBy, setSortBy] = useState('created_desc');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/proforma-invoices`, { params: { scope } });
      setItems(r.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Summary is scope-agnostic — always shows full picture regardless of the
  // currently selected tab (all / pending / mine). Kept independent so users
  // switching tabs don't lose the big-picture context.
  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const r = await axios.get(`${API}/proforma-invoices/summary`);
      setSummary(r.data);
    } catch (e) {
      console.error('Error fetching PI summary:', e);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);
  useEffect(() => { loadSummary(); }, []);

  const handleDecision = async () => {
    if (decision.action === 'reject' && !decisionReason.trim()) {
      notify('Rejection reason is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/proforma-invoices/${decision.pi.id}/${decision.action}`,
        { reason: decisionReason.trim() }
      );
      setDecision(null);
      setDecisionReason('');
      load();
      loadSummary();
    } catch (e) {
      notify(e.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const openPDF = (pi) => {
    const token = localStorage.getItem('token') || '';
    window.open(`${API}/proforma-invoices/${pi.id}/pdf?token=${token}`, '_blank');
  };

  const buildPiSharePayload = async (pi) => {
    const resp = await axios.post(`${API}/proforma-invoices/${pi.id}/generate-share-link`);
    const { share_token } = resp.data;
    const pdfUrl = `${BACKEND_URL}/api/share/pi/${share_token}`;
    const cust = pi.customer_snapshot || {};
    return {
      subject: `Proforma Invoice ${pi.pi_number} — ${cust.organisation_name || ''}`.trim(),
      body: `Hello,\n\nPlease find Proforma Invoice ${pi.pi_number} attached below.\n\nCustomer: ${cust.organisation_name || '—'}\nAmount: ₹${Number(pi.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}\n\nView / download the invoice PDF here:\n${pdfUrl}\n\nBest regards,`,
    };
  };

  // Feb 2026 (iter79 + iter86) — President-only: repair legacy PIs whose
  // stored `total_amount` is 0 (iter79) AND legacy PIs missing `quotation_number`
  // / `customer_snapshot` (iter86). Idempotent — safe to click many times.
  const [repairing, setRepairing] = useState(false);
  const handleBackfillAmounts = async () => {
    const ok = await confirm({
      title: 'Repair legacy PI records?',
      body: 'This will (a) recompute ₹ amount for any PI stored as 0 and (b) fill missing Quote number / Customer for any PI where those columns show "—". Safe to run multiple times — already-correct PIs are skipped.',
      confirmText: 'Repair',
    });
    if (!ok) return;
    setRepairing(true);
    try {
      const [amounts, display] = await Promise.all([
        axios.post(`${API}/proforma-invoices/backfill-amounts`),
        axios.post(`${API}/proforma-invoices/backfill-display-fields`),
      ]);
      notify(`${amounts.data.message || ''}\n${display.data.message || ''}`.trim());
      await Promise.all([load(), loadSummary()]);
    } catch (e) {
      notify(e.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  const tabs = [
    { key: 'all', label: 'All PIs' },
    { key: 'pending', label: 'Pending My Approval' },
    { key: 'mine', label: 'My PIs' },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="pi-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#E2F3F0] flex items-center justify-center text-[#00a69c]">
          <Receipt size={28} weight="bold" />
        </div>
        <div className="flex-1">
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
            Proforma Invoices
          </h1>
          <p className="text-[#6D8E87]">Generated from accepted quotations. Each PI requires one additional approval step.</p>
        </div>
        {user?.role === 'President' && (
          <button
            type="button"
            onClick={handleBackfillAmounts}
            disabled={repairing}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-xs font-semibold hover:bg-amber-100 disabled:opacity-60"
            title="One-shot cleanup: recompute ₹ amount for any PI stored as 0 (fixes PIs generated before iter79 field-name bug)."
            data-testid="pi-backfill-amounts-btn"
          >
            <Wrench size={14} weight="bold" />
            {repairing ? 'Repairing…' : 'Repair legacy amounts'}
          </button>
        )}
      </div>

      <SummaryCards
        testid="pi-summary"
        loading={summaryLoading}
        items={summary ? [
          {
            label: 'Total issued',
            value: inr(summary.total_issued_value),
            icon: CurrencyInr,
            tone: 'revenue',
            sub: `${summary.invoice_count} invoice${summary.invoice_count === 1 ? '' : 's'}`,
            testid: 'summary-pi-issued',
          },
          {
            label: 'Pending approval',
            value: inr(summary.pending_approval_value),
            icon: Clock,
            tone: summary.pending_approval_count > 0 ? 'warning' : 'default',
            sub: `${summary.pending_approval_count} awaiting`,
            testid: 'summary-pi-pending',
          },
          {
            label: 'This month',
            value: inr(summary.this_month_value),
            icon: CalendarBlank,
            sub: 'issued so far',
            testid: 'summary-pi-month',
          },
        ] : []}
      />

      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#D5E8E4]">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setScope(t.key)}
            className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm transition ${
              scope === t.key
                ? 'border-[#00a69c] text-[#00a69c]'
                : 'border-transparent text-[#6D8E87] hover:text-[#052A23]'
            }`}
            data-testid={`pi-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <PipelineToolbar
        pageKey="proforma"
        window={closeWindow}
        onWindowChange={setCloseWindow}
        sortBy={sortBy}
        onSortChange={setSortBy}
        showForecastToggle={false}
      />

      <div className="medical-card overflow-x-auto">
        <table className="w-full min-w-[1000px]" data-testid="pi-table">
          <thead className="bg-[#F4F9F7]">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">PI Number</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Quote</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Customer</th>
              <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Amount</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Created By</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Approver</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Status</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Apply close-window + sort on top of the server-returned list.
              let list = items;
              if (closeWindow && closeWindow !== 'all') {
                const win = computeCloseWindow(closeWindow);
                list = list.filter(p => withinCloseWindow(p, 'expected_closing_date', win));
              }
              list = sortRows(list, sortBy, { valueKey: 'total_amount', dateKey: 'expected_closing_date' });
              if (loading) {
                return <TableRowsSkeleton cols={8} />;
              }
              if (list.length === 0) {
                return <tr><td colSpan={8}><EmptyState compact title="No Proforma Invoices in this view" hint="Generate a PI from an approved quotation." testid="no-pis" /></td></tr>;
              }
              return list.map(p => {
              const isApprover = p.approver_role === user?.role && p.status === 'pending_approval';
              const cust = p.customer_snapshot || {};
              return (
                <tr key={p.id} className="border-t border-[#D5E8E4] hover:bg-[#F4F9F7]" data-testid={`pi-row-${p.id}`}>
                  <td className="px-4 py-3 text-sm font-bold text-[#052A23]" data-testid={`pi-number-${p.id}`}>{p.pi_number}</td>
                  <td className="px-4 py-3 text-sm text-[#3B5A54]">{p.quotation_number || '—'}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="text-[#052A23] font-medium">{cust.organisation_name || '—'}</div>
                    <div className="text-xs text-[#6D8E87]">{cust.customer_code || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-[#052A23]">
                    ₹ {Number(p.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    <div
                      className="text-[10px] font-normal italic text-[#6D8E87] mt-0.5"
                      title={inrInWords(p.total_amount)}
                      data-testid={`pi-amount-in-words-${p.id}`}
                    >
                      {inrInWords(p.total_amount)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="text-[#052A23]">{p.created_by_name}</div>
                    <div className="text-xs text-[#6D8E87]">{p.created_by_role}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {p.approver_name ? (
                      <>
                        <div className="text-[#052A23]">{p.approver_name}</div>
                        <div className="text-xs text-[#6D8E87]">{p.approver_role}</div>
                      </>
                    ) : (
                      <span className="text-xs italic text-[#6D8E87]">Awaiting {p.approver_role}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[p.status]}`} data-testid={`pi-status-${p.id}`}>
                      {p.status.replace('_', ' ').toUpperCase()}
                    </span>
                    {(p.revision || 1) > 1 && (
                      <span className="ml-1 text-[10px] font-semibold text-[#6D8E87]" data-testid={`pi-revision-${p.id}`}>Rev {p.revision}</span>
                    )}
                    {p.status === 'rejected' && p.approver_decision_reason && (
                      <div className="mt-1 text-[11px] text-rose-700 max-w-[220px] break-words" data-testid={`pi-reject-reason-${p.id}`}>
                        {p.approver_decision_reason}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap items-center">
                      <button
                        onClick={() => openPDF(p)}
                        className="flex items-center gap-1 px-2 py-1 border border-[#00a69c] text-[#00a69c] rounded text-xs font-medium hover:bg-[#E2F3F0]"
                        data-testid={`pi-pdf-${p.id}`}
                      ><FilePdf size={12} /> PDF</button>
                      {p.status === 'approved' && (
                        <ShareMenu
                          variant="icon"
                          testid={`pi-share-${p.id}`}
                          getPayload={() => buildPiSharePayload(p)}
                        />
                      )}
                      {p.status === 'rejected' && (p.created_by === user?.id || ['Regional Sales Manager', 'National Sales Manager'].includes(user?.role)) && (
                        <button
                          onClick={async () => {
                            try {
                              await resubmitPi(p.id);
                              load();
                            } catch (e) {
                              notify(piErrorMessage(e, 'Could not resubmit PI'));
                            }
                          }}
                          className="flex items-center gap-1 px-2 py-1 bg-rose-600 text-white rounded text-xs font-medium hover:bg-rose-700"
                          data-testid={`pi-resubmit-${p.id}`}
                        >Revise &amp; Resubmit</button>
                      )}
                      {isApprover && (
                        <>
                          <button
                            onClick={() => { setDecision({ pi: p, action: 'approve' }); setDecisionReason(''); }}
                            className="flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700"
                            data-testid={`pi-approve-${p.id}`}
                          ><Check size={12} /> Approve</button>
                          <button
                            onClick={() => { setDecision({ pi: p, action: 'reject' }); setDecisionReason(''); }}
                            className="flex items-center gap-1 px-2 py-1 bg-rose-600 text-white rounded text-xs font-medium hover:bg-rose-700"
                            data-testid={`pi-reject-${p.id}`}
                          ><X size={12} /> Reject</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            });
            })()}
          </tbody>
        </table>
      </div>

      {/* Decision Modal */}
      <Dialog open={!!decision} onOpenChange={(o) => { if (!o) { setDecision(null); setDecisionReason(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#052A23] capitalize" style={{ fontFamily: 'Outfit' }}>
              {decision?.action} Proforma Invoice
            </DialogTitle>
          </DialogHeader>
          {decision && (
            <div className="mt-4 space-y-4">
              <div className="p-3 bg-[#F4F9F7] rounded-lg text-sm">
                <div className="font-bold text-[#052A23]">{decision.pi.pi_number}</div>
                <div className="text-xs text-[#6D8E87] mt-1">{decision.pi.customer_snapshot?.organisation_name}</div>
                <div className="text-xs text-[#3B5A54] mt-1">₹ {Number(decision.pi.total_amount).toLocaleString('en-IN')}</div>
                <div className="text-[11px] italic text-[#6D8E87] mt-1">
                  {inrInWords(decision.pi.total_amount)}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">
                  Reason for {decision.action} {decision.action === 'reject' ? '*' : '(optional)'}
                </label>
                <textarea
                  value={decisionReason}
                  onChange={(e) => setDecisionReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  placeholder={decision.action === 'approve' ? 'Optional approval note' : 'Why are you rejecting this?'}
                  data-testid="pi-decision-reason"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setDecision(null); setDecisionReason(''); }}
                  className="px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm text-[#3B5A54] hover:bg-[#F4F9F7]"
                  data-testid="pi-cancel-decision">Cancel</button>
                <button onClick={handleDecision} disabled={submitting}
                  className={`px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 ${
                    decision.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'
                  }`}
                  data-testid="pi-confirm-decision"
                >{submitting ? 'Submitting...' : `Confirm ${decision.action}`}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProformaInvoices;
